<script setup>
import DatePicker from 'primevue/datepicker';
import { computed, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    width: {
        type: String,
        default: '16rem'
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '1rem'
    },
    visible: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['update:modelValue', 'date-select', 'input', 'change']);

const dateValue = ref(props.modelValue ? props.modelValue.split(',').map((d) => new Date(d)) : null);
const datePickerRef = ref(null);

watch(
    () => props.modelValue,
    (newValue) => {
        dateValue.value = newValue
            ? newValue
                  .split(',')
                  .map((d) => {
                      const date = new Date(d);
                      return isNaN(date.getTime()) ? null : date;
                  })
                  .filter(Boolean)
            : null;
    }
);
const defaultPlaceholder = computed(() => {
    return props.placeholder || 'ex)  ' + formatDate(new Date()); // 오늘 날짜를 기본값으로 설정
});

const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const onDateSelect = (event) => {
    let formattedDates;
    if (Array.isArray(dateValue.value)) {
        formattedDates = dateValue.value
            .filter((date) => date instanceof Date && !isNaN(date.getTime()))
            .map(formatDate)
            .join(',');
    } else if (event instanceof Date && !isNaN(event.getTime())) {
        formattedDates = formatDate(event);
    } else {
        formattedDates = '';
    }
    emit('update:modelValue', formattedDates);
    emit('date-select', formattedDates);
};

const onChange = (event) => {
    let formattedDates;
    if (Array.isArray(event.value)) {
        formattedDates = event.value
            .filter((date) => date instanceof Date && !isNaN(date.getTime()))
            .map(formatDate)
            .join(',');
    } else if (event.value instanceof Date && !isNaN(event.value.getTime())) {
        formattedDates = formatDate(event.value);
    } else {
        formattedDates = '';
    }
    emit('update:modelValue', formattedDates);
    emit('change', formattedDates);
};

const openDatePicker = () => {
    if (datePickerRef.value) {
        datePickerRef.value.overlayVisible = true;
    }
};

const onTodayClick = () => {
    const today = new Date();
    dateValue.value = [today, today];
    const formattedDate = `${formatDate(today)},${formatDate(today)}`;
    emit('update:modelValue', formattedDate);
    emit('change', formattedDate);
};

const onClearClick = () => {
    dateValue.value = null;
    emit('update:modelValue', '');
    emit('change', '');
};
</script>

<template>
    <div
        v-if="visible"
        class="flex items-center relative"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label :class="['label-text', required ? 'label-required' : 'label-default']" :style="{ marginRight: labelRightmargin }">
            {{ label }}
        </label>

        <div class="relative flex items-center w-full">
            <DatePicker
                ref="datePickerRef"
                v-model="dateValue"
                :inputId="inputId"
                :placeholder="defaultPlaceholder"
                :disabled="disabled"
                @change="onChange"
                @date-select="onDateSelect"
                class="custom-datepicker"
                :class="{ 'w-full': width === '16rem' }"
                :style="{ width: width !== '100%' ? width : 'auto' }"
                dateFormat="yy-mm-dd"
                selectionMode="range"
                :manualInput="false"
            >
                <template #footer>
                    <div class="p-datepicker-buttonbar">
                        <button type="button" class="p-button-text custom-today-button" @click="onTodayClick">Today</button>
                        <button type="button" class="p-button-text custom-clear-button" @click="onClearClick">Clear</button>
                    </div>
                </template>
            </DatePicker>
            <span class="date-icon pi pi-calendar" @click="openDatePicker"></span>
        </div>
    </div>
</template>

<style scoped>
.label-text {
    font-family: 'SCoreDream500';
    font-size: 0.95rem;
    color: #374151;
    width: 100%;
}

.label-required {
    color: #f87171; /* red-500 */
}

.label-required::after {
    content: '*';
    color: #f87171;
    margin-left: 0.25rem;
    margin-right: 0.2rem;
}

.custom-datepicker :deep(.p-inputtext) {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 0rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    background-color: white;
    color: #111827;
    text-align: center;
    height: 2.5rem;
}

.custom-datepicker :deep(.p-inputtext:focus) {
    border-color: #0038ea !important;
    outline: none;
}

.custom-datepicker :deep(.p-inputtext:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
}

.date-icon {
    position: absolute;
    right: 1rem; /* 오른쪽 여백 */
    top: 50%;
    transform: translateY(-50%);
    font-size: 1rem;
    color: #6b7280; /* text-gray-500 */
    cursor: pointer;
}
.custom-today-button {
    color: black !important;
}

.custom-clear-button {
    color: black !important;
}
</style>
